import { useState, useEffect, useCallback } from 'react';
import { Users, Tag, Layers } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '../../../../../ui/radio-group';
import { Checkbox } from '../../../../../ui/checkbox';
import { Label } from '../../../../../ui/label';
import { epicengageSupabase } from '../../../../../../integrations/supabase/client';
import { cn } from '../../../../../../lib/utils';

const PRESETS = [
  { value: 'ALL', label: 'All Customers', description: 'Send to everyone in your list' },
  { value: 'NEW', label: 'New Customers', description: 'Joined in the last 30 days' },
  { value: 'ACTIVE', label: 'Active Customers', description: 'Activity in the last 30 days' },
  { value: 'INACTIVE', label: 'Inactive Customers', description: 'No activity for 30+ days' },
] as const;

type Segment = { id: string; name: string };
type Tag = { id: string; name: string; color: string | null };

export interface AudienceSelection {
  preset: string | null;
  segmentIds: string[];
  tagIds: string[];
}

interface AudienceSelectorProps {
  merchantId: string;
  value: AudienceSelection;
  onChange: (value: AudienceSelection) => void;
  recipientCount: number;
  onRecipientCountChange: (count: number) => void;
  suppressedCount?: number;
  onSuppressedCountChange?: (count: number) => void;
}

export function AudienceSelector({
  merchantId,
  value,
  onChange,
  recipientCount,
  onRecipientCountChange,
  suppressedCount = 0,
  onSuppressedCountChange,
}: AudienceSelectorProps) {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);

  // Fetch segments and tags on mount
  useEffect(() => {
    if (!merchantId) return;

    const fetchData = async () => {
      const [segRes, tagRes] = await Promise.all([
        epicengageSupabase.from('segments').select('id, name').eq('merchant_id', merchantId).order('name'),
        epicengageSupabase.from('tags').select('id, name, color').eq('merchant_id', merchantId).order('name'),
      ]);
      setSegments((segRes.data || []) as Segment[]);
      setTags((tagRes.data || []) as Tag[]);
    };
    fetchData();
  }, [merchantId]);

  // Compute recipient count whenever selection changes
  const computeCount = useCallback(async () => {
    if (!merchantId) return;

    const { preset, segmentIds, tagIds } = value;
    const hasSegments = segmentIds.length > 0;
    const hasTags = tagIds.length > 0;

    let customerIds: string[] | null = null;

    if (hasSegments) {
      const { data } = await epicengageSupabase
        .from('customer_segments')
        .select('customer_id')
        .eq('merchant_id', merchantId)
        .in('segment_id', segmentIds);
      customerIds = [...new Set((data || []).map(r => r.customer_id))];
      if (customerIds.length === 0) {
        onRecipientCountChange(0);
        onSuppressedCountChange?.(0);
        return;
      }
    }

    if (hasTags) {
      const { data } = await epicengageSupabase
        .from('customer_tags')
        .select('customer_id')
        .eq('merchant_id', merchantId)
        .in('tag_id', tagIds);
      const tagCustomerIds = new Set((data || []).map(r => r.customer_id));

      if (customerIds !== null) {
        customerIds = customerIds.filter(id => tagCustomerIds.has(id));
      } else {
        customerIds = [...tagCustomerIds];
      }

      if (customerIds.length === 0) {
        onRecipientCountChange(0);
        onSuppressedCountChange?.(0);
        return;
      }
    }

    let query = epicengageSupabase
      .from('customers')
      .select('*', { count: 'exact', head: true })
      .eq('merchant_id', merchantId)
      .neq('email', '');

    if (!hasSegments && preset) {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      if (preset === 'NEW') {
        query = query.gte('created_at', thirtyDaysAgo.toISOString());
      } else if (preset === 'ACTIVE') {
        query = query.eq('status_tag', 'ACTIVE');
      } else if (preset === 'INACTIVE') {
        query = query.eq('status_tag', 'INACTIVE');
      }
    }

    if (customerIds !== null) {
      query = query.in('id', customerIds);
    }

    const { count: totalCount } = await query;
    const total = totalCount || 0;

    // Count suppressed within this audience
    let suppressedQuery = epicengageSupabase
      .from('customer_suppressions')
      .select('customer_id', { count: 'exact', head: true })
      .eq('merchant_id', merchantId)
      .eq('channel', 'EMAIL');

    if (customerIds !== null) {
      suppressedQuery = suppressedQuery.in('customer_id', customerIds);
    }

    const { count: supCount } = await suppressedQuery;
    const suppressed = supCount || 0;

    onSuppressedCountChange?.(suppressed);
    onRecipientCountChange(total - suppressed);
  }, [merchantId, value, onRecipientCountChange, onSuppressedCountChange]);

  useEffect(() => {
    computeCount();
  }, [computeCount]);

  const hasSegments = value.segmentIds.length > 0;

  const handlePresetChange = (preset: string) => {
    onChange({ ...value, preset, segmentIds: [] });
  };

  const toggleSegment = (segmentId: string) => {
    const next = value.segmentIds.includes(segmentId)
      ? value.segmentIds.filter(id => id !== segmentId)
      : [...value.segmentIds, segmentId];
    onChange({ ...value, segmentIds: next, preset: next.length > 0 ? null : value.preset || 'ALL' });
  };

  const toggleTag = (tagId: string) => {
    const next = value.tagIds.includes(tagId)
      ? value.tagIds.filter(id => id !== tagId)
      : [...value.tagIds, tagId];
    onChange({ ...value, tagIds: next });
  };

  return (
    <div className="space-y-6">
      {/* Section A: Quick Presets */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground uppercase tracking-wider">
          <Users className="h-4 w-4" />
          Quick Presets
        </h4>
        <RadioGroup
          value={value.preset || ''}
          onValueChange={handlePresetChange}
          disabled={hasSegments}
        >
          <div className="grid grid-cols-2 gap-3">
            {PRESETS.map((p) => (
              <Label
                key={p.value}
                className={cn(
                  'flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors',
                  hasSegments && 'opacity-50 cursor-not-allowed',
                  !hasSegments && value.preset === p.value
                    ? 'border-primary bg-primary/5'
                    : 'hover:bg-muted',
                )}
              >
                <RadioGroupItem value={p.value} className="mt-0.5" />
                <div>
                  <div className="font-medium text-sm">{p.label}</div>
                  <div className="text-xs text-muted-foreground">{p.description}</div>
                </div>
              </Label>
            ))}
          </div>
        </RadioGroup>
        {hasSegments && (
          <p className="text-xs text-muted-foreground italic">
            Presets are disabled while segments are selected.
          </p>
        )}
      </div>

      {/* Section B: Segments */}
      {segments.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground uppercase tracking-wider">
            <Layers className="h-4 w-4" />
            Segments
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {segments.map((seg) => (
              <Label
                key={seg.id}
                className={cn(
                  'flex items-center gap-2.5 p-3 border rounded-lg cursor-pointer transition-colors',
                  value.segmentIds.includes(seg.id)
                    ? 'border-primary bg-primary/5'
                    : 'hover:bg-muted',
                )}
              >
                <Checkbox
                  checked={value.segmentIds.includes(seg.id)}
                  onCheckedChange={() => toggleSegment(seg.id)}
                />
                <span className="text-sm font-medium">{seg.name}</span>
              </Label>
            ))}
          </div>
        </div>
      )}

      {/* Section C: Tags */}
      {tags.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground uppercase tracking-wider">
            <Tag className="h-4 w-4" />
            Filter by Tags
          </h4>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <Label
                key={tag.id}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 border rounded-full cursor-pointer transition-colors text-sm',
                  value.tagIds.includes(tag.id)
                    ? 'border-primary bg-primary/5 font-medium'
                    : 'hover:bg-muted',
                )}
              >
                <Checkbox
                  checked={value.tagIds.includes(tag.id)}
                  onCheckedChange={() => toggleTag(tag.id)}
                  className="h-3.5 w-3.5"
                />
                {tag.name}
              </Label>
            ))}
          </div>
        </div>
      )}

      {/* Estimated Recipients */}
      <div className="space-y-2">
        <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Estimated Recipients</span>
          <span className="text-lg font-bold">{recipientCount}</span>
        </div>
        {suppressedCount > 0 && (
          <div className="p-2 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800 flex items-center justify-between">
            <span>Suppressed (unsubscribed)</span>
            <span className="font-semibold">{suppressedCount}</span>
          </div>
        )}
        {suppressedCount > 0 && (
          <p className="text-xs text-muted-foreground italic">
            Suppressed contacts are excluded automatically (e.g., unsubscribed).
          </p>
        )}
      </div>
    </div>
  );
}
